import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination, Transition } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
// import Delete from "./delete" // kalau kamu punya delete GR, enable
import { getBranchList } from "helpers/general-helper"

// optional: kalau kamu mau bikin filter seperti PO, bisa reuse komponen Filter yg sama
// import Filter from "pages/purchase/filter"

export default function List() {
  const { can, currentBranch } = useAuth()
  const { title, transition, setTransition } = useOutletContext()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  const searchParam = searchParams.get("search") ?? ""

  // Local state untuk search (debounced biar gak spam history)
  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)
  const searchDebounceRef = useRef(null)

  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()
  const branchIdList = useMemo(() => {
    if (!branchSelection) return []
    return branchSelection.map((x) => x.id).filter(Boolean)
  }, [branchSelection])

  /**
   * ✅ ResultSet untuk GR
   * Ganti "goods_receipts" kalau nama RS kamu beda.
   *
   * Params yang umum:
   * - outlet_id
   * - branch_list
   * - page
   * - record_per_page
   * - search
   * - status
   */
  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `goods-receipts`,
    {
      outlet_id: currentBranch?.id,
      branch_list: JSON.stringify(branchIdList),
      page,
      per_page: 5,
      search: searchParam || undefined,
      status: status || undefined,
    }
  )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })

    // reset page ketika filter berubah
    if (
      patch &&
      (Object.prototype.hasOwnProperty.call(patch, "search") ||
        Object.prototype.hasOwnProperty.call(patch, "status"))
    ) {
      next.delete("page")
    }

    setSearchParams(next, opts)
  }

  const removeFilter = () => setSearchParams({})

  const onSearchChange = (val) => {
    setSearch(val)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      patchSearchParams({ search: val })
    }, 350)
  }

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  // normalize list dari response baru
  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data?.data
    return []
  }, [result_set])

  // untuk Pagination component (kalau komponen kamu masih expect fields ini)
  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.pagination?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium"
    if (s === "POSTED") return <span className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "DRAFT") return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED" || s === "CANCELED") return <span className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  const formatDateTime = (value) => {
    if (!value) return "-"

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : null,
      // credentials: "include",
    })

    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      const msg = json?.message || "Request gagal"
      const err = json?.error ? `: ${json.error}` : ""
      throw new Error(msg + err)
    }
    return json
  }

  const [actingId, setActingId] = useState(null)

  const canPost = (gr) => String(gr?.status || "").toUpperCase() === "DRAFT"
  const canCancel = (gr) => !["CANCELLED", "POSTED"].includes(String(gr?.status || "").toUpperCase())

  const onPost = async (gr) => {
    if (!gr?.id) return
    if (!window.confirm(`POST Goods Receipt ${gr.gr_number || ""}?`)) return

    try {
      setActingId(gr.id)
      await postJson(`/goods-receipts/${gr.id}/post`)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal post GR")
    } finally {
      setActingId(null)
    }
  }

  const onCancel = async (gr) => {
    if (!gr?.id) return
    const reason = window.prompt(`Cancel GR ${gr.gr_number || ""}\nAlasan (optional):`, "")
    if (reason === null) return

    try {
      setActingId(gr.id)
      await postJson(`/goods-receipts/${gr.id}/cancel`, reason ? { reason } : null)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal cancel GR")
    } finally {
      setActingId(null)
    }
  }

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            {/* Search */}
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  strokeWidth={1}
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>

              <input
                ref={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                value={search}
                type="text"
                placeholder={`${lang.search} Goods Receipt`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            {/* Simple filter status (tanpa komponen Filter) */}
            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">All Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="POSTED">POSTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>

            {(status || searchParam) && (
              <button
                type="button"
                onClick={removeFilter}
                className="hidden sm:inline-flex px-3 py-3 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-xl hover:bg-neutral-50"
              >
                Reset
              </button>
            )}

            {/* Kalau mau pakai Filter component seperti PO:
                <Filter ... onSubmit={(v)=>patchSearchParams(v)} onRemove={removeFilter} />
            */}
          </div>

          {/* <div className="flex items-center space-x-2">
            {can("goods-receipt.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => setTransition({ type: "slide", reverse: true })}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div> */}
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  GR No
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  PO No
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Outlet
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Receipt Date
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Status
                </th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Items
                </th>
                {/* <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">
                  Qty
                </th> */}
                <th className="relative px-3 py-3">
                  <span className="sr-only">{lang.action}</span>
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && !searchParam && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && searchParam && (
                <tr className="text-center">
                  <td colSpan={8} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {!isLoading &&
                list.map((gr) => (
                  <tr key={gr.id}>
                    <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                      {gr.gr_number ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {gr?.purchase_order?.po_number ?? gr?.po_number_snapshot ?? gr.po_id ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {gr.branches?.name ?? gr.outlet_id ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {formatDateTime(gr.receipt_date) ?? "-"}
                    </td>

                    <td className="px-3 py-4 text-xs whitespace-nowrap">{statusBadge(gr.status)}</td>

                    <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {gr.items_count ?? gr.items?.length ?? "-"}
                    </td>

                    {/* <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                      {gr.qty_total ?? "-"}
                    </td> */}

                    <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        {(can("goods-receipt.update") || can("goods-receipt.post")) && (
                          <Link
                            to={`${gr.id}/edit`}
                            state={{ back: title, from: location?.pathname, transition: "slide" }}
                            className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90"
                          >
                            <Pencil className="w-6 h-6" />
                          </Link>
                        )}

                        {can("goods-receipt.post") && canPost(gr) && (
                          <button
                            type="button"
                            disabled={actingId === gr.id}
                            onClick={() => onPost(gr)}
                            className="px-3 py-2 text-[11px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-60"
                          >
                            {actingId === gr.id ? "..." : "Post"}
                          </button>
                        )}

                        {/* {can("goods-receipt.cancel") && canCancel(gr) && (
                          <button
                            type="button"
                            disabled={actingId === gr.id}
                            onClick={() => onCancel(gr)}
                            className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                          >
                            {actingId === gr.id ? "..." : "Cancel"}
                          </button>
                        )} */}

                        {/* Kalau ada delete permission & komponen delete:
                            {can("goods-receipt.delete") && <Delete data={gr} onSuccess={mutate} />}
                        */}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination params={generatePaginationParam()} links={paginationLinks} from={from} to={to} total={total} />
      </div>
    </Transition>
  )
}
